import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Bell, X, Send, Eye, User, Phone, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatNotifications = () => {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [response, setResponse] = useState('');
  const [responding, setResponding] = useState(false);
  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();

    // Auto-refresh every 5 seconds
    const interval = setInterval(() => {
      fetchMessages();
      fetchUnreadCount();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Show notification when new messages arrive
  useEffect(() => {
    if (unreadCount > 0) {
      setShowNewMessageAlert(true);
      // Auto-hide after 3 seconds
      const timer = setTimeout(() => {
        setShowNewMessageAlert(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Fetch messages error:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Fetch unread count error:', error);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/chat/messages/${messageId}/read`);
      fetchMessages();
      fetchUnreadCount();
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  };

  const handleRespond = async (messageId) => {
    if (!response.trim()) {
      toast.error('Shkruani një përgjigje');
      return;
    }

    setResponding(true);
    try {
      await axios.post(`${API}/chat/messages/${messageId}/respond`, {
        message_id: messageId,
        response: response
      });
      
      toast.success('Përgjigja u dërgua me sukses');
      setResponse('');
      setSelectedMessage(null);
      fetchMessages();
      fetchUnreadCount();
    } catch (error) {
      toast.error('Gabim në dërgimin e përgjigjes');
      console.error('Response error:', error);
    } finally {
      setResponding(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL') + ' në ' + date.toLocaleTimeString('sq-AL');
  };

  return (
    <>
      {/* Notification Bell Button */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative bg-blue-600 hover:bg-blue-700 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-2 -right-2 bg-red-500 text-white min-w-[20px] h-5 rounded-full text-xs flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* New Message Alert */}
      {showNewMessageAlert && unreadCount > 0 && (
        <div className="fixed top-20 right-4 z-40 animate-bounce">
          <Card className="bg-red-50 border-red-200 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-800 font-medium">
                  {unreadCount} mesazh{unreadCount > 1 ? 'e' : ''} të ri nga klientët
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewMessageAlert(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Messages Popup */}
      {showNotifications && (
        <div className="fixed top-16 right-4 w-96 max-h-[600px] z-50">
          <Card className="shadow-xl">
            <CardHeader className="bg-blue-600 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center text-lg">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Mesazhet nga Klientët
                    {unreadCount > 0 && (
                      <Badge className="ml-2 bg-red-500">
                        {unreadCount}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-blue-100">
                    Mesazhet e fundit nga klientët
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNotifications(false)}
                  className="text-white hover:bg-blue-700"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-0">
              <div className="max-h-96 overflow-y-auto">
                {messages.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Nuk ka mesazhe nga klientët</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {messages.slice(0, 10).map((message) => (
                      <div key={message.id} className={`p-4 ${!message.is_read ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="font-medium text-sm">{message.client_name}</span>
                            {!message.is_read && (
                              <Badge className="bg-red-500 text-white text-xs">I ri</Badge>
                            )}
                          </div>
                          <div className="flex items-center text-xs text-slate-500">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDateTime(message.created_at)}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 mb-2">
                          <Phone className="w-3 h-3 text-slate-500" />
                          <span className="text-xs text-slate-600">{message.client_phone}</span>
                          {message.ticket_number && (
                            <Badge variant="outline" className="text-xs">
                              {message.ticket_number}
                            </Badge>
                          )}
                        </div>

                        <p className="text-sm text-slate-700 mb-3 bg-slate-100 p-2 rounded">
                          {message.message.length > 100 
                            ? `${message.message.substring(0, 100)}...` 
                            : message.message
                          }
                        </p>

                        {message.admin_response && (
                          <div className="mb-2">
                            <p className="text-xs text-slate-600 mb-1">Përgjigja juaj:</p>
                            <p className="text-sm text-slate-700 bg-green-100 p-2 rounded border-l-4 border-green-500">
                              {message.admin_response}
                            </p>
                          </div>
                        )}

                        <div className="flex space-x-2">
                          {!message.is_read && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => markAsRead(message.id)}
                              className="text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Lexuar
                            </Button>
                          )}
                          {!message.admin_response && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => setSelectedMessage(message)}
                              className="text-xs"
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Përgjigju
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Response Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Send className="w-5 h-5 mr-2" />
                Përgjigju Mesazhit
              </CardTitle>
              <CardDescription>
                Mesazh nga: {selectedMessage.client_name} ({selectedMessage.client_phone})
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Mesazhi origjinal:</label>
                <p className="text-slate-600 bg-slate-100 p-3 rounded mt-1">
                  {selectedMessage.message}
                </p>
              </div>
              
              <div>
                <label htmlFor="response">Përgjigja juaj:</label>
                <Textarea
                  id="response"
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Shkruani përgjigjen tuaj këtu..."
                  rows={4}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => handleRespond(selectedMessage.id)}
                  disabled={responding}
                  className="flex-1"
                >
                  {responding ? 'Duke dërguar...' : 'Dërgo Përgjigjen'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedMessage(null);
                    setResponse('');
                  }}
                >
                  Anulo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default ChatNotifications;